// Global quick-add state (owner 2026-08-17): the navbar + menu opens the
// create form ALONE in an app-level overlay drawer instead of navigating to
// /<module>/forms/drawer/new, which mounts the module grid with the drawer
// beside it. Entity is one of 'company' | 'contact' | 'deal' | 'prospect'
// (bulk-prospects keeps navigating — it is a full page, not a form).
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

/**
 * @typedef {'company' | 'contact' | 'deal' | 'prospect'} QuickAddEntity
 * @typedef {{
 *   entity: QuickAddEntity | null,
 *   seed: Record<string, unknown> | null,
 *   openQuickAdd: (entity: QuickAddEntity, seed?: Record<string, unknown> | null) => void,
 *   closeQuickAdd: () => void
 * }} QuickAddValue
 */

/** @type {React.Context<QuickAddValue | null>} */
const QuickAddContext = createContext(null)

/**
 * Null-safe: callers fall back to route navigation when no provider is mounted.
 * @returns {QuickAddValue | null}
 */
export function useQuickAdd() {
  return useContext(QuickAddContext)
}

export function QuickAddProvider({ children }) {
  const [entity, setEntity] = useState(null)
  // Optional pre-scope for the create form (e.g. the company form's "+ Deal"
  // seeds { deals__account }) — merged over the new-record defaults by the
  // entity's DrawerNew.
  const [seed, setSeed] = useState(null)

  const openQuickAdd = useCallback((nextEntity, nextSeed = null) => {
    setSeed(nextSeed)
    setEntity(nextEntity)
  }, [])
  const closeQuickAdd = useCallback(() => {
    setEntity(null)
    setSeed(null)
  }, [])

  const value = useMemo(
    () => ({ entity, seed, openQuickAdd, closeQuickAdd }),
    [entity, seed, openQuickAdd, closeQuickAdd]
  )

  return <QuickAddContext.Provider value={value}>{children}</QuickAddContext.Provider>
}
